import { HustleupSmallIcon } from "@images/svgIcons/hustleUpSmallIcon"
import { word } from "@modules/core/utils/i18n"
import { HUText } from "@modules/ui/components/huText"
import { HURow } from "@modules/ui/components/layout"
import { animatedSkeletonStyle } from "@modules/ui/styles/animation"
import { getMonthDates } from "@modules/utils/dateUtils"
import styled, { DefaultTheme, useTheme } from "styled-components"

type CardType = "Primary" | "Secondary"
type Props = {
  cardType: CardType
  cardTitle: string
  value?: string | boolean
  loading: boolean
  period: string
} & React.HTMLAttributes<HTMLDivElement>

export const FinanceTotalMonthCard: React.FC<Props> = ({
  cardType,
  cardTitle,
  value = "--",
  loading,
  period,
  ...props
}) => {
  const theme = useTheme()
  const color = cardType === "Primary" ? theme.buttonPrimaryTextColor : theme.textPrimary
  const { firstDate, lastDate } = getMonthDates(period)
  return (
    <Container $cardType={cardType} $loading={loading} {...props}>
      <HURow align gap={10}>
        <Dot />
        <HUText fontStyle="BL" color={color}>
          {cardTitle}
        </HUText>
      </HURow>
      {value && (
        <HUText fontStyle="DS" color={color}>
          {value}
        </HUText>
      )}
      <HUText fontStyle="BM" color={theme.neutralColor600}>
        {`${firstDate} ${word("global.periodInterval.to")} ${lastDate}`}
      </HUText>
      <HustleupSmallIconStyled />
    </Container>
  )
}

const Container = styled.div<{ $cardType: CardType; $loading: boolean }>`
  height: 172px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: ${({ $cardType, theme }) =>
    $cardType === "Primary"
      ? `linear-gradient(90deg, ${theme.primaryColor} 0%, ${theme.primaryColor600} 100%)`
      : theme.surfaceCard};
  padding: 25px 28px 20px 28px;
  border-radius: 33px;
  gap: 10px;
  overflow: hidden;

  ${({ $cardType, $loading, theme }) =>
    $loading &&
    ($cardType === "Primary"
      ? animatedSkeletonStyle(theme as DefaultTheme, theme.primaryColor, theme.primaryColor600)
      : animatedSkeletonStyle(theme as DefaultTheme, theme.surfaceCard))}
`

const HustleupSmallIconStyled = styled(HustleupSmallIcon)`
  position: absolute;
  top: 0;
  right: 0;
  opacity: ${({ theme }) => theme.financeCardBackgroundIconOpacity};
`

const Dot = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 5.6px;
  background: ${({ theme }) => theme.textPrimary};
`
